import React, { FC, ReactNode, useEffect, useState } from "react";
import { IconClockGlyph, IconRefreshGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { MonoError, ProgressSweep } from "../../../videos/selectedVideoCardV2/performance/ScoreState";
import { btnClass, glyph } from "../components/ui";
import { AlertStrip, RecessedStrip, WorkingStrip } from "../components/Strips";
import { POLL_NOTE, elapsedLabel, failureCopy, queuedAgo, scoreHelper, workingCopy } from "./pageModel";
import type { PageView, PdpUrl } from "./pageModel";

/**
 * The page block's status strip, by view: "Not scored yet", the working strip
 * (scoring · queued · delayed), the alert strip (failed-newer ·
 * failed-never), or Scoring paused on a page never scored. Nothing for the
 * rest. The block puts it in its slot; the strips and their live regions are
 * the elements they always were.
 *
 * Its own component because it ticks: the elapsed counter every second while
 * a sync score runs, "Queued N min ago" every minute while delayed. Only the
 * strip re-renders on a tick, not the whole block (its score, trend, rows
 * and ⋮ menu).
 */

/** Re-render every `ms` while `on` — the elapsed counter (1s) and "Queued N min ago" (60s). */
const useTick = (on: boolean, ms: number) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const timer = window.setInterval(() => setTick((n) => n + 1), ms);
    return () => window.clearInterval(timer);
  }, [on, ms]);
};

interface Props {
  row: PdpUrl;
  view: PageView;
  /** `canScoreSync(role)`: the working copy's wording. */
  sync: boolean;
  /** `hasScore(row)`. */
  scored: boolean;
  /** `isInternal(role)`: the failure's raw token. */
  internal: boolean;
  /** When this page's sync Score now was clicked (ms): the elapsed counter counts from it. */
  scoringStartedAt: number | null;
  /** The failure strip's Retry: held as the score button is. */
  scoreDisabled: boolean;
  onScore: () => void;
}

export const PageStatus: FC<Props> = ({ row, view, sync, scored, internal, scoringStartedAt, scoreDisabled, onScore }) => {
  useTick(view === "scoring", 1000);
  useTick(view === "delayed", 60000);

  if (view === "unscored") {
    return (
      <div className="flex items-center gap-3.5 border-t border-border-token pt-4">
        <span
          className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 bg-surface-2 text-text-2"
          aria-hidden="true"
        >
          <IconClockGlyph size={16} strokeWidth={1.8} />
        </span>
        <div className="flex flex-col gap-[3px] min-w-0">
          <span className="font-CircularStdBlack text-text-1" style={{ fontSize: 13.5 }}>
            Not scored yet
          </span>
          <span className="text-text-2" style={{ fontSize: 12.5 }}>
            {scoreHelper(sync)}
          </span>
        </div>
      </div>
    );
  }

  const working = workingCopy(view, sync, scored);
  if (working) {
    // Only the ticking m:ss is tabular (no jitter); the words stay proportional.
    let meta: ReactNode;
    if (view === "scoring") {
      const elapsed = scoringStartedAt ? (Date.now() - scoringStartedAt) / 1000 : 0;
      meta = (
        <>
          <span className="tabular-nums">{elapsedLabel(elapsed)}</span> elapsed
        </>
      );
    } else if (view === "delayed") meta = queuedAgo(row.latest_attempt);
    return (
      <WorkingStrip
        title={working.title}
        body={working.body}
        poll={view === "queued" ? POLL_NOTE : undefined}
        meta={meta}
        footer={view === "scoring" ? <ProgressSweep /> : undefined}
      />
    );
  }

  const failure = failureCopy(view, row.latest_attempt);
  if (failure) {
    // The raw token is for staff only: a known one prints small beside the
    // sentence, an unknown one (an unbounded string) gets the mono block.
    const showToken = internal && !!failure.token;
    return (
      <AlertStrip
        title={failure.title}
        body={
          <>
            {failure.body}
            {showToken && failure.known && (
              <span className="ml-2 font-mono" style={{ fontSize: 11, opacity: 0.8 }}>
                {failure.token}
              </span>
            )}
          </>
        }
        action={
          <button type="button" onClick={onScore} disabled={scoreDisabled} className={btnClass("onAlert", { disabled: scoreDisabled })}>
            <IconRefreshGlyph size={13} strokeWidth={2} />
            Retry
          </button>
        }
        footer={showToken && !failure.known ? <MonoError text={failure.token as string} /> : undefined}
      />
    );
  }

  if (view === "paused" && !scored) {
    return (
      <RecessedStrip
        icon={glyph("pause", 16, 1.8)}
        title="Scoring paused"
        body="This page won't be scored until you resume. It hasn't been scored yet."
      />
    );
  }
  return null;
};
